/* ═══════════ ВЫПАДАЮЩИЙ СПИСОК — ОДИН НА ВЕСЬ КАБИНЕТ (прототип: assets/dd.js) — кабинет тренера и админка ═══════════
   Системный список браузера выглядит по-разному в Chrome и Safari и не ищет. Страницы по-прежнему
   пишут обычный <select> (value, onChange, <optgroup>), а этот модуль прячет его и ставит рядом
   кнопку: по ней открывается наш список. Выбор пишется в <select> и шлёт change — React-обработчики
   страниц не меняются. Поиск — в списках длиннее DD_SEARCH_FROM пунктов. Подпись справа — data-sub
   у <option>. Оставить системный список — data-native у <select>.

   Отличие от прототипа — React: он меняет выбранный пункт (option.selected), пункты и disabled без
   события change, поэтому подпись кнопки сверяется по изменениям DOM и по записи option.selected;
   <select>, который React убрал, уносит с собой и кнопку. */

const DD_SEARCH_FROM = 5;
type Sel = HTMLSelectElement & { _dd?: DdBtn };
type DdBtn = HTMLButtonElement & { _sel?: Sel };

const esc = (s: unknown) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
const nrm = (s: string) => String(s || '').toLowerCase().replace(/ё/g, 'е').trim();
const CHEV = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 6.5l4 4 4-4"/></svg>';
const CHK = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.9" aria-hidden="true"><path d="M3.5 8.5 6.5 11.5 12.5 5"/></svg>';
const SRCH = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><circle cx="7" cy="7" r="4.5"/><path d="M10.5 10.5 14 14"/></svg>';

/* Открыт всегда один */
let OPEN: { box: HTMLDivElement; btn: DdBtn; sel: Sel; off: (e: Event) => void; key: (e: KeyboardEvent) => void } | null = null;

const label = (sel: Sel) => {
  const o = sel.options[sel.selectedIndex];
  return o ? `${esc(o.textContent)}${o.dataset.sub ? `<s>${esc(o.dataset.sub)}</s>` : ''}` : '';
};
function sync(sel: Sel) {
  const b = sel._dd;
  if (!b) return;
  const v = b.querySelector('.dselv')!;
  const h = label(sel);
  if (v.innerHTML !== h) v.innerHTML = h;
  b.disabled = sel.disabled;
  /* «Отдых», «Все папки» — приглушённо */
  b.classList.toggle('ph', !sel.value);
}
/* Несколько записей подряд (React перебирает все option) — одна сверка */
const queued = new Set<Sel>();
function later(sel: Sel | null | undefined) {
  if (!sel?._dd || queued.has(sel)) return;
  queued.add(sel);
  queueMicrotask(() => {
    queued.delete(sel);
    sync(sel);
  });
}

/** Кнопка вместо <select>: та же ширина (инлайн-стиль переезжает), у поля формы (.inp) — на всю ширину */
function enhance(root: ParentNode) {
  root.querySelectorAll<Sel>('select:not([data-dsel]):not([data-native])').forEach((sel) => {
    sel.dataset.dsel = '1';
    const b = document.createElement('button') as DdBtn;
    b.type = 'button';
    b.className = 'dsel' + (sel.classList.contains('inp') ? ' dsel-inp' : '');
    if (sel.getAttribute('style')) b.setAttribute('style', sel.getAttribute('style')!);
    if (sel.id) b.dataset.for = sel.id;
    if (sel.hasAttribute('tabindex')) b.tabIndex = sel.tabIndex;
    b.setAttribute('aria-haspopup', 'listbox');
    const lab = sel.getAttribute('aria-label') || sel.title;
    if (lab) b.setAttribute('aria-label', lab);
    if (sel.title) b.title = sel.title;
    b.innerHTML = `<span class="dselv"></span>${CHEV}`;
    sel._dd = b;
    b._sel = sel;
    sel.after(b);
    sync(sel);
    sel.addEventListener('change', () => sync(sel));
  });
}

const onWin = () => close();
const onScroll = (e: Event) => {
  if (OPEN && !OPEN.box.contains(e.target as Node)) place();
};
function close(back?: boolean) {
  if (!OPEN) return;
  const { box, btn, off } = OPEN;
  OPEN = null;
  box.remove();
  btn.classList.remove('open');
  btn.setAttribute('aria-expanded', 'false');
  document.removeEventListener('mousedown', off, true);
  removeEventListener('resize', onWin);
  removeEventListener('scroll', onScroll, true);
  if (back && btn.isConnected) btn.focus({ preventScroll: true });
}

/** Под кнопкой, а не помещается вниз — над ней. Ширина — не уже кнопки */
function place() {
  if (!OPEN) return;
  const { box, btn } = OPEN;
  const r = btn.getBoundingClientRect();
  /* кнопку спрятали (строка связки без курсора) */
  if (!r.width) return close();
  box.style.minWidth = Math.max(240, r.width) + 'px';
  const h = box.offsetHeight,
    below = innerHeight - r.bottom - 8,
    up = below < h && r.top > below;
  box.style.left = Math.max(8, Math.min(r.left, innerWidth - box.offsetWidth - 8)) + scrollX + 'px';
  box.style.top = (up ? r.top - h - 6 : r.bottom + 6) + scrollY + 'px';
  box.classList.toggle('up', up);
}

function open(btn: DdBtn) {
  const sel = btn._sel;
  if (!sel || sel.disabled) return;
  if (OPEN && OPEN.btn === btn) return close(true);
  close();
  const items = [...sel.options].map((o, i) => ({
    i,
    t: o.textContent || '',
    sub: o.dataset.sub || '',
    dis: o.disabled,
    g: o.parentElement?.tagName === 'OPTGROUP' ? (o.parentElement as HTMLOptGroupElement).label : '',
  }));
  const search = items.length > DD_SEARCH_FROM && !sel.hasAttribute('data-nosearch');
  const box = document.createElement('div');
  box.className = 'sug dsl';
  box.setAttribute('role', 'listbox');
  box.innerHTML =
    (search ? `<label class="dsel-s">${SRCH}<input placeholder="Поиск…" autocomplete="off" spellcheck="false" aria-label="Поиск в списке"></label>` : '') + '<div class="dsel-list"></div>';
  document.body.appendChild(box);
  const list = box.querySelector<HTMLDivElement>('.dsel-list')!;
  const inp = box.querySelector('input');
  const draw = (q: string) => {
    const qq = nrm(q);
    const rows = items.filter((x) => !qq || nrm(x.t + ' ' + x.sub).includes(qq));
    let g: string | null = null,
      h = '';
    rows.forEach((x) => {
      if (x.g && x.g !== g) {
        g = x.g;
        h += `<div class="dsel-cap">${esc(g)}</div>`;
      }
      const cur = x.i === sel.selectedIndex;
      h +=
        `<button type="button" class="row${cur ? ' cur' : ''}" role="option" aria-selected="${cur}" data-i="${x.i}"${x.dis ? ' disabled' : ''}>` +
        `<span class="dsel-t">${esc(x.t)}</span>${x.sub ? `<s>${esc(x.sub)}</s>` : ''}${cur ? CHK : ''}</button>`;
    });
    list.innerHTML = h || '<div class="dsel-none">Ничего не нашли</div>';
    const on = (!qq && list.querySelector('.row.cur')) || list.querySelector('.row:not([disabled])');
    if (on) {
      on.classList.add('on');
      on.scrollIntoView({ block: 'nearest' });
    }
  };
  draw('');
  const off = (e: Event) => {
    if (!box.contains(e.target as Node) && !btn.contains(e.target as Node)) close();
  };
  const pick = (i: number) => {
    close(true);
    if (i === sel.selectedIndex) return;
    sel.selectedIndex = i;
    sel.dispatchEvent(new Event('input', { bubbles: true }));
    sel.dispatchEvent(new Event('change', { bubbles: true }));
  };
  const key = (e: KeyboardEvent) => {
    const rows = [...list.querySelectorAll<HTMLButtonElement>('.row:not([disabled])')];
    const k = rows.findIndex((x) => x.classList.contains('on'));
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      return close(true);
    }
    if (e.key === 'Tab') return close();
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!rows.length) return;
      if (k >= 0) rows[k].classList.remove('on');
      const n = (k + (e.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length;
      rows[n].classList.add('on');
      rows[n].scrollIntoView({ block: 'nearest' });
      return;
    }
    if (e.key === 'Enter' || (e.key === ' ' && !inp)) {
      e.preventDefault();
      e.stopPropagation();
      const r = rows[k] || rows[0];
      if (r) pick(+r.dataset.i!);
    }
  };
  OPEN = { box, btn, sel, off, key };
  btn.classList.add('open');
  btn.setAttribute('aria-expanded', 'true');
  place();
  document.addEventListener('mousedown', off, true);
  addEventListener('resize', onWin);
  addEventListener('scroll', onScroll, true);
  /* Без поиска фокус остаётся на кнопке, клавиши с неё уходят в список */
  if (inp) {
    inp.focus({ preventScroll: true });
    inp.addEventListener('input', () => {
      draw(inp.value);
      place();
    });
  }
  /* фокус остаётся в поиске */
  box.addEventListener('mousedown', (e) => {
    if ((e.target as Element).closest('.row')) e.preventDefault();
  });
  box.addEventListener('click', (e) => {
    const r = (e.target as Element).closest<HTMLElement>('.row:not([disabled])');
    if (r) pick(+r.dataset.i!);
  });
  box.addEventListener('mousemove', (e) => {
    const r = (e.target as Element).closest('.row:not([disabled])');
    if (!r || r.classList.contains('on')) return;
    list.querySelectorAll('.row.on').forEach((x) => x.classList.remove('on'));
    r.classList.add('on');
  });
  box.addEventListener('keydown', key);
}

let installed = false;
export function installDropdowns() {
  if (installed) return;
  installed = true;
  /* React выбирает пункт записью option.selected — подпись кнопки сверяем после неё */
  const d = Object.getOwnPropertyDescriptor(HTMLOptionElement.prototype, 'selected')!;
  Object.defineProperty(HTMLOptionElement.prototype, 'selected', {
    ...d,
    set(v: boolean) {
      d.set!.call(this, v);
      later((this as HTMLOptionElement).parentElement?.closest('select') as Sel | null);
    },
  });
  const v = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')!;
  Object.defineProperty(HTMLSelectElement.prototype, 'value', {
    ...v,
    set(x: string) {
      v.set!.call(this, x);
      later(this as Sel);
    },
  });

  document.addEventListener('click', (e) => {
    const b = (e.target as Element).closest?.<DdBtn>('button.dsel');
    if (b) {
      e.preventDefault();
      open(b);
    }
  });
  document.addEventListener(
    'keydown',
    (e) => {
      if (OPEN && e.target === OPEN.btn) return OPEN.key(e);
      const b = (e.target as Element).closest?.<DdBtn>('button.dsel');
      if (b && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        open(b);
      }
    },
    true,
  );
  /* Новые <select> подхватываем сразу, до отрисовки кадра — системный список не успевает мелькнуть.
     Сменились пункты или disabled — сверяем подпись; <select> убрали — убираем и кнопку */
  const mo = new MutationObserver((ms) => {
    for (const m of ms) {
      if (m.type === 'attributes') {
        later((m.target as Element).closest('select') as Sel | null);
        continue;
      }
      const sel = (m.target as Element).closest?.('select') as Sel | null;
      if (sel) later(sel);
      for (const n of m.addedNodes)
        if (n.nodeType === 1) {
          const el = n as Element;
          if (el.tagName === 'SELECT') enhance(el.parentElement!);
          else if (el.querySelector('select:not([data-dsel])')) enhance(el);
        }
      for (const n of m.removedNodes)
        if (n.nodeType === 1) {
          const el = n as Element;
          const gone = el.tagName === 'SELECT' ? [el as Sel] : [...el.querySelectorAll<Sel>('select[data-dsel]')];
          for (const s of gone) if (s._dd && !s.isConnected) s._dd.remove();
        }
    }
    if (OPEN && !OPEN.btn.isConnected) close();
  });
  const start = () => {
    enhance(document);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
  };
  if (document.body) start();
  else addEventListener('DOMContentLoaded', start);
}
